import { useEffect } from 'react'
import { Sparkles, AlertCircle, CheckCircle2, Info, ChevronRight } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card'
import { cn } from '@/core/lib/utils'
import { useIsMounted } from '@/core/hooks/useIsMounted'

import { useAIStore } from '../stores/aiStore'

export function AIInsightsWidget() {
  const suggestions = useAIStore((s) => s.suggestions)
  const insights = useAIStore((s) => s.insights)
  const loadSuggestions = useAIStore((s) => s.loadSuggestions)
  const loadInsights = useAIStore((s) => s.loadInsights)
  const mounted = useIsMounted()

  useEffect(() => {
    void (async () => {
      await loadSuggestions()
      await loadInsights()
      if (!mounted.current) {
        return
      }
    })()
  }, [loadSuggestions, loadInsights, mounted])

  const iconMap = {
    alert: AlertCircle,
    suggestion: Sparkles,
    info: Info,
  }

  const colorMap = {
    alert: 'text-red-500 bg-red-50',
    suggestion: 'text-primary bg-orange-50',
    info: 'text-blue-500 bg-blue-50',
  }

  return (
    <div className="space-y-4">
      {/* Suggestions */}
      {suggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg font-semibold text-navy">AI Suggestions</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2.5 pt-0">
            {suggestions.map((s) => {
              const Icon = iconMap[s.type]
              return (
                <div
                  key={s.id}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer"
                >
                  <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', colorMap[s.type])}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{s.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary transition-colors shrink-0 mt-1" />
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <CardTitle className="text-lg font-semibold text-navy">AI Insights</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {insights.map((insight, i) => (
              <div key={`insight-${i}-${insight.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-navy">
                <span className="text-primary mt-0.5">•</span>
                <span>{insight}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
